import {
  ALLOCATION_TYPES,
  CommunityOrAggregatorTypes,
  EnergyManagementTypes,
  MARKET_RATES,
  meterInstallationTypes,
} from "utils/constants";
import { formatDate } from "utils/time";

const COMMUNITY_TYPES = ["PROD", "DEMO", "SIMULATION", "CUSTOM", "TEST"].map((value) => ({
  value,
  label: value,
}));

const SETTLEMENT_TYPES = ["INDIVIDUAL", "AGGREGATOR"].map((value) => ({
  value,
  label: value,
}));

export const VERSION_ENTITY_META = {
  config: {
    title: "Community configuration",
    singular: "configuration version",
    icon: "settings",
    fields: [
      {
        key: "method_alloc_coeff",
        label: "Allocation method",
        input: "select",
        options: ALLOCATION_TYPES,
      },
      {
        key: "market_rate",
        label: "Market mechanism",
        input: "select",
        options: MARKET_RATES,
      },
      { key: "market_divisor", label: "Market divisor", type: "number" },
      { key: "market_compensation", label: "Market compensation", type: "number" },
      { key: "market_small_increment", label: "Market small increment", type: "number" },
      { key: "market_fixed_price", label: "Market fixed price", type: "number" },
      { key: "lem_trading_fee", label: "LEM trading fee", type: "number" },
      {
        key: "settlement_sale_price_type",
        label: "Settlement sale-price type",
        input: "select",
        options: SETTLEMENT_TYPES,
      },
      {
        key: "transaction_adjustment_tolerance",
        label: "Transaction adjustment tolerance",
        type: "number",
      },
      { key: "status", label: "Status", type: "status" },
      {
        key: "type",
        label: "Community type",
        input: "select",
        options: COMMUNITY_TYPES,
      },
      {
        key: "energy_management_type",
        label: "Energy-management type",
        input: "select",
        options: EnergyManagementTypes,
      },
      {
        key: "community_or_aggregator_type",
        label: "Community/aggregator type",
        input: "select",
        options: CommunityOrAggregatorTypes,
      },
    ],
  },
  membership: {
    title: "Membership versions",
    singular: "membership version",
    icon: "person",
    fields: [
      { key: "status", label: "Status", type: "status" },
      { key: "is_host", label: "Host", type: "boolean" },
      { key: "is_promoter", label: "Promoter", type: "boolean" },
    ],
  },
  meter: {
    title: "Meter versions",
    singular: "meter version",
    icon: "electric_meter",
    fields: [
      {
        key: "installation_type",
        label: "Installation type",
        input: "select",
        options: meterInstallationTypes,
      },
      { key: "production_identifier", label: "Production identifier" },
      { key: "ord", label: "Grid operator" },
      { key: "cpe_id", label: "Parent CPE", type: "meter" },
      { key: "status", label: "Status", type: "status" },
    ],
  },
  resource: {
    title: "Resource versions",
    singular: "resource version",
    icon: "bolt",
    fields: [
      { key: "name", label: "Name" },
      { key: "status", label: "Status", type: "status" },
      { key: "meter_id", label: "Meter", type: "meter" },
    ],
  },
  ownership: {
    title: "Ownership versions",
    singular: "ownership version",
    icon: "pie_chart",
    fields: [{ key: "perc_ownership", label: "Ownership percentage", type: "number" }],
  },
  contract: {
    title: "Contract versions",
    singular: "contract version",
    icon: "receipt_long",
    fields: [],
  },
  resp: {
    title: "RESP relationship versions",
    singular: "RESP version",
    icon: "lan",
    fields: [{ key: "is_active", label: "Active", type: "boolean" }],
  },
  fixedBilateral: {
    title: "Fixed bilateral price versions",
    singular: "fixed bilateral price version",
    icon: "sell",
    fields: [{ key: "price", label: "Price", type: "number" }],
  },
  ems: {
    title: "EMS configuration versions",
    singular: "EMS configuration version",
    icon: "memory",
    fields: [
      { key: "horizon", label: "Horizon", type: "number" },
      { key: "delta_t", label: "Time step", type: "number" },
      { key: "strict_pos_coeffs", label: "Strict positive coefficients", type: "boolean" },
      { key: "total_share_coeffs", label: "Total-share coefficients", type: "boolean" },
      { key: "is_active", label: "Active", type: "boolean" },
      { key: "config_data", label: "Module configuration", type: "json" },
    ],
  },
};

export const getVersionEntityMeta = (entityType, t) => {
  const meta = VERSION_ENTITY_META[entityType];
  if (!meta) return null;

  return {
    ...meta,
    title: t(`versioning.entities.${entityType}.title`, { defaultValue: meta.title }),
    singular: t(`versioning.entities.${entityType}.singular`, { defaultValue: meta.singular }),
    fields: meta.fields.map((field) => ({
      ...field,
      label: t(`versioning.entities.${entityType}.fields.${field.key}`, {
        defaultValue: field.label,
      }),
      options: field.options?.map((option) => ({
        ...option,
        label: t(`versioning.optionValues.${option.value}`, { defaultValue: option.label }),
      })),
    })),
  };
};

const translatedStatus = (value, t) =>
  t(`versioning.optionValues.${value}`, { defaultValue: value || "—" });

// Dates follow the browser locale (see utils/time formatDate).
export const formatVersionDate = (value) => (value ? formatDate(value, null) : null);

export const versionStatus = (version, today, t) => {
  if (!version.start_date) {
    return { label: t("versioning.status.invalidDate"), tone: "red" };
  }
  if (version.start_date > today) {
    return { label: t("versioning.status.scheduled"), tone: "info" };
  }
  if (version.end_date && version.end_date < today) {
    return { label: t("versioning.status.expired"), tone: "gray" };
  }
  return { label: t("versioning.status.current"), tone: "green" };
};

export const versionSummary = (entityType, row, t) => {
  if (entityType === "resource") return `${row.name} · ${translatedStatus(row.status, t)}`;
  if (entityType === "membership" || entityType === "meter") {
    return translatedStatus(row.status, t);
  }
  if (entityType === "ownership") return `${row.perc_ownership}%`;
  if (entityType === "resp") {
    return row.is_active
      ? t("versioning.optionValues.ACTIVE")
      : t("versioning.optionValues.INACTIVE");
  }
  if (entityType === "fixedBilateral") return String(row.price);
  if (entityType === "ems") return `${row.module_type || "EMS"} · ${row.horizon}h`;
  if (entityType === "contract") return `${row.buy_type || "—"} / ${row.sell_type || "—"}`;
  if (row.status) return translatedStatus(row.status, t);
  return row.market_rate || t("versioning.summary.configuration");
};
